import * as React from "react";

import { Model , _ } from "@flowable/forms";
import { isNil } from "lodash";

import FlowableNumberErrorComponent from "../Common/ErrorComponent/FlowableNumberErrorComponent";

const UNCOMPLETE_NUMBER = /([\.eE]$|\..*0$)/;	// eslint-disable-line no-useless-escape

export default class NumberComponent extends Model.StatefulFormComponent {
	constructor (props) {
		super(props);

		this.state = {
			focused: false,
			touched: false,
			enteredZero: false,
			validation: {
				numberError: false,
				fractionError: false,
				emptyError: false,
				minError: false,
				maxError: false
			}
		};

		this.allowedDecimals = _.get(props.config, "extraSettings.fractionSize") || 0;
		this.format = NumberComponent.getNumberFormat(props.config, props.lang);

		this.handleBlur = this.handleBlur.bind(this);
		this.setTouched = this.setTouched.bind(this);
		this.preventLetters = this.preventLetters.bind(this);
		this.handleChange = this.handleChange.bind(this);
	}

	componentDidUpdate (prevProps) {
		if (
			prevProps.lang !== this.props.lang ||
      _.get(prevProps.config, "extraSettings.numberFormat") !== _.get(this.props.config, "extraSettings.numberFormat")
		) {
			this.format = NumberComponent.getNumberFormat(this.props.config, this.props.lang);
		}
	}

	componentDidMount () {
		const { config } = this.props;

		if (config.value === 0 && config.defaultValue === 0) {
			this.setState({ enteredZero: true });
		}
	}

	preventLetters (event) {
		const keyCode = event.keyCode || event.which;
		const keyValue = String.fromCharCode(keyCode);
		const decimalValue =
			(this.allowedDecimals && this.format && getRegExp(this.format.decimal)) ||
			(this.allowedDecimals && getRegExp(".")) || "";
		const isAllowedCharacter = new RegExp(`^[0-9${decimalValue}]$`).test(keyValue);
		const decimalSeparator = this.format?.decimal || ".";
		const isInvalidCharacter = !isAllowedCharacter && keyValue !== "-" && keyValue !== decimalSeparator;

		const hasMinus = event.target.value.includes("-");
		const isMinusAtWrongPosition = keyValue === "-" && (hasMinus || event.target.selectionStart !== 0);

		const hasDecimalSeparator = event.target.value.includes(decimalSeparator);
		const isInvalidDecimalSeparator = keyValue === decimalSeparator && hasDecimalSeparator;

		if (isMinusAtWrongPosition || isInvalidDecimalSeparator || isInvalidCharacter) {
			event.preventDefault();
		}
	}

	formatNumber (config, isDisabled) {
		let value = config.value;
		let firstFocus = !this.state.firstFocus;
		if (value === 0 && !this.state.enteredZero) {
			value = "0";
		}
		if (!value && value !== 0) {return "";}
		let stringValue = Object.is(value, -0) ? "-0" : value.toString();
		stringValue = stringValue.replace(/^(-?)0+(?=\d)/, "$1");
		const expressionValue = this.formatExpressionValue();
		if (expressionValue !== null) {
			return expressionValue;
		}
		if (isDisabled) {
			stringValue = this.round(stringValue);
		}
		// Case when we don't have format, like simple 123456.78
		if (!this.format) {
			if (this.state.focused && stringValue.endsWith(".")) {
				return stringValue;
			}
			const numSplitted = stringValue.split(".");
			let floated = numSplitted[1] || "";
			if (this.allowedDecimals > 0 && (!this.state.focused || firstFocus)) {floated = floated.padEnd(this.allowedDecimals, "0");}

			return numSplitted[0].concat(floated !== "" ? "." + floated : "");
		}

		if (config.extraSettings.numberType === "integer") {
			// For integer we need only thousand format, like 123,456,789
			const rgxSep = new RegExp("(.+)" + getRegExp(this.format.thousand) + "$");
			const intFormatReversed = reverse(stringValue)
				.replace(/(\d{3})(?=\d)/g, "$1" + this.format.thousand)
				.replace(rgxSep, "$1");

			return reverse(intFormatReversed);
		} else if (config.extraSettings.numberType === "float") {
			const hasDecimal = String(config.value).includes(".");
			const rgxSep = new RegExp("(.+)" + getRegExp(this.format.thousand) + "$");
			// to be able to add a decimal char in the end when you edit a field
			if (this.state.focused && (stringValue.endsWith(this.format.decimal) || stringValue.endsWith("."))) {
				const intFormatReversed = reverse(stringValue)
					.replace(/(\d{3})(?=\d)/g, "$1" + this.format.thousand)
					.replace(rgxSep, "$1");
				let res = reverse(intFormatReversed);
				res = res.slice(0, res.length - 1) + this.format.decimal;

				return res;
			}

			// extra case for other decimal delimiter (not a dot), we must replace that by format.decimal from the conf
			if (hasDecimal && this.format.decimal !== ".") {
				const lastDotIndex = stringValue.lastIndexOf(".");
				const part1 = stringValue.slice(0, lastDotIndex);
				const part2 = lastDotIndex === stringValue.length - 1 ? "" : stringValue.slice(lastDotIndex + 1);
				stringValue = `${part1}${this.format.decimal}${part2}`;
			}

			// Split by format.decimal
			const numSplitted = stringValue.toString().split(this.format.decimal);

			// Apply thousands delimiters
			const intFormatReversed = reverse(numSplitted[0])
				.replace(/(\d{3})(?=\d)/g, "$1" + this.format.thousand)
				.replace(rgxSep, "$1");
			const intFormatted = reverse(intFormatReversed);

			let floated = numSplitted[1] || "";
			if (this.allowedDecimals > 0 && (!this.state.focused || firstFocus)) {floated = floated.padEnd(this.allowedDecimals, "0");}

			return intFormatted.concat(floated !== "" ? this.format.decimal + floated : "");
		}

		return stringValue;
	}

	round (value) {
		const config = this.props.config;
		const fractionSize = config.extraSettings?.fractionSize;
		const decimalPart = value.toString().split(".")[1];
		const decimalPartLength = decimalPart?.length;

		return decimalPartLength && fractionSize && decimalPartLength > fractionSize
			? parseFloat(value).toFixed(fractionSize)
			: value;
	}

	formatExpressionValue () {
		const originalValue = this.props.config.$original?.value;
		if (originalValue && /[+\-*/]/.test(originalValue)) {
			const expressionResult = calculateExpression(originalValue, this.props.config.$scope);
			let stringValue = this.round(expressionResult).toString();

			if (this.props.config.extraSettings.numberType === "float" && this.allowedDecimals > 0) {
				if (this.format) {
					const decimalSeparator = this.format.decimal;
					const thousandSeparator = this.format.thousand;
					const [ intPart, decPart = "" ] = stringValue.split(".");
					const paddedDecPart = decPart.padEnd(this.allowedDecimals, "0");
					const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
					stringValue = intFormatted + decimalSeparator + paddedDecPart;
				} else {
					const [ intPart, decPart = "" ] = stringValue.split(".");
					const paddedDecPart = decPart.padEnd(this.allowedDecimals, "0");
					stringValue = intPart + "." + paddedDecPart;
				}
			}

			return stringValue;
		}

		return null;
	}

	resetValidationState () {
		this.setState({
			validation: {
				numberError: false,
				fractionError: false,
				minError: false,
				maxError: false
			}
		});
	}

	setValidationError (errorType, value) {
		this.setState(prevState => ({
			validation: {
				...prevState.validation,
				[errorType]: value,
			},
		}));
	}

	handleBlur (event) {
		const input = event.target;
		const cleanValue = NumberComponent.cleanNumber(input.value, this.format);
		const toNumber = cleanValue.length ? Number(cleanValue) : undefined;
		this.setFocused(false);
		this.setTouched();
		this.resetValidationState();
		if (cleanValue) {
			let value = toNumber !== undefined && !isNaN(toNumber) ? toNumber : cleanValue;
			let valueStr = `${value}`;

			this.validateNumber(valueStr);
			const { fractionSize } = this.props.config.extraSettings;
			const decimalSeparator = this.format && this.format.decimal ? this.format.decimal : ".";
			const decimalPart = valueStr.split(decimalSeparator)[1];
			if (decimalPart && decimalPart.length > fractionSize) {
				this.setValidationError("fractionError",true);
			}

			this.props.onChange(value);
		}
	}

	validateNumber (value) {
		const { config } = this.props;
		const { min, max, numberType } = config.extraSettings;

		const rgx = /^(?:-?[0-9][0-9.,'\s]*|)$/;
		if ((numberType === "integer" || numberType === "float") && !rgx.test(value)) {
			this.setValidationError("numberError", true);
		} else {
			let numericValue;

			if (numberType === "integer") {
				numericValue = Number(value.replace(/[',.\s]/g, ""));
			} else if (numberType === "float") {
				const cleanValue = isNaN(value) ? NumberComponent.cleanNumber(value, this.format) : value;
				numericValue = Number(cleanValue);
			}

			if (min !== undefined && numericValue < min) {
				this.setValidationError("minError", true);
			} else if (max !== undefined && numericValue > max) {
				this.setValidationError("maxError", true);
			}
		}
	}

	handleChange (event) {
		const { config, onChange } = this.props;
		const { fractionSize } = config.extraSettings;
		let value = event.target.value;

		this.setState({ enteredZero: value === "0" || value === "-0" });
		let editCount = this.state.firstFocus;
		this.setState({ firstFocus: editCount + 1 });
		this.resetValidationState();

		if (isNil(value)) {
			config.value = undefined;
			config.$scope[config.$path] = 0;
			value = "";
		}

		this.validateNumber(value);
		const decimalSeparator = this.format && this.format.decimal ? this.format.decimal : ".";
		const decimalPart = value.split(decimalSeparator)[1];
		if (decimalPart && decimalPart.length > fractionSize) {
			value = value.slice(0, value.length - (decimalPart.length - fractionSize));
			this.setValidationError("fractionError", true);
		}

		onChange(NumberComponent.$parse(value, config, this.props.lang));
	}

	getNumberFormatted (isDisabled) {
		return this.formatNumber(this.props.config, isDisabled);
	}

	setFocused (isFocused) {
		this.setState({ focused: isFocused });
		this.setState({ firstFocus: 0 });
	}

	setTouched () {
		if (!this.state.touched) {
			this.setState({ touched: true });
		}
	}

	render () {
		const { config, Components, forceValidations } = this.props;
		const { focused, touched, validation } = this.state;
		const { extraSettings, value, customValidations } = config;
		const disabled = config.enabled === false || !config.$path;
		const bem = this.bem("number", {
			disabled,
			focused,
			empty: _.no(value)
		});
		const placeholder = this.getFromConfig("placeholder");
		const isEmpty = this.isRequired(config) && _.no(value);
		if (isEmpty) {
			this.setValidationError("emptyError", true);
		}

		return (
			<div data-flw-id={config.id} className={bem()}>
				<Components.label {...this.props} className={bem("label")} htmlFor={this.getDomId()} />
				<input
					id={this.getDomId()}
					className={bem("input")}
					type="text"
					step="any"
					placeholder={placeholder}
					onKeyPress={this.preventLetters}
					value={this.getNumberFormatted(disabled)}
					onChange={this.handleChange}
					onFocus={() => {
						this.dispatchFocusEvent();
						this.setFocused(true);
					}}
					onBlur={event => {
						this.dispatchBlurEvent();
						this.handleBlur(event);
					}}
					disabled={disabled}
				/>
				<Components.description {...this.props} className={bem("description")} />

				{(forceValidations || touched) && (
					<FlowableNumberErrorComponent
						extraSettings={extraSettings}
						customValidations={customValidations}
						empty={validation.emptyError}
						numberError={validation.numberError}
						fractionError={validation.fractionError}
						minError={this.hasMinError(validation, config)}
						maxError={this.hasMaxError(validation, config)}
					/>
				)}
			</div>
		);
	}

	hasMaxError (validation, config) {
		return validation.maxError || (Array.isArray(config.$errors) && config.$errors.includes("maxValue"));
	}

	hasMinError (validation, config) {
		return validation.minError || (Array.isArray(config.$errors) && config.$errors.includes("minValue"));
	}

	isRequired (config) {
		return Array.isArray(config.$errors) && config.$errors.includes("isRequired");
	}

	static $parse = (value, config, lang) => {
		if (_.no(value) || typeof value === "number") {return value;}

		const cleanValue = NumberComponent.cleanNumber(value, NumberComponent.getNumberFormat(config, lang));

		if (!cleanValue.length) {return "";} // field is empty
		if (UNCOMPLETE_NUMBER.test(cleanValue)) {return cleanValue;} // number is maybe unfinished
		const toNumber = Number(cleanValue);

		return !isNaN(toNumber) ? toNumber : cleanValue;
	};

	static getNumberFormat (config, lang) {
		let numberFormat = _.get(config, "extraSettings.numberFormat");
		if (numberFormat === "auto") {
			try {
				// use formatToParts to get the lang numberFormat
				const parts = new Intl.NumberFormat(lang.replace("_", "-")).formatToParts(10000.1);
				numberFormat = parts.reduce((obj, p) => {
					if (p.type === "group" || p.type === "decimal") {
						obj.push(p.value);
					}

					return obj;
				}, []);
			} catch (error) {
				return undefined;
			}
		}
		if (numberFormat === "(space),") {
			return {
				thousand: " ",
				decimal: ","
			};
		}
		if (!numberFormat || numberFormat.length !== 2) {return undefined;}

		return {
			thousand: numberFormat[0],
			decimal: numberFormat[1]
		};
	}

	static cleanNumber (value, format) {
		if (!format) {return value;}
		const intSep = getRegExp(format.thousand);
		const floatSep = getRegExp(format.decimal);

		return value.replace(new RegExp(intSep, "g"), "").replace(new RegExp(floatSep, "g"), ".");
	}
}

function reverse (value) {
	return value
		.split("")
		.reverse()
		.join("");
}

function getRegExp (symbol) {
	let sym = symbol;
	switch (symbol) {
	case ".":
		sym = "\\.";
		break;
	case ",":
		sym = ",";
		break;
	case " ":
		sym = "\\s";
		break;
	case "'":
		sym = "'";
		break;
	default:
		sym = symbol;
		break;
	}

	return sym;
}

function calculateExpression (expression, context) {
	try {
		let cleanExpr = expression.replace(/{{|}}/g, "").trim();

		if (!cleanExpr.match(/[+\-*/]/)) {
			const value = context[cleanExpr];

			return value === undefined || value === null ? 0 : Number(value);
		}

		const tokens = cleanExpr.split(/([+\-*/])/).map(token => token.trim());

		const filteredTokens = tokens.filter(token => token !== "");

		if (filteredTokens.length === 0) {
			return 0;
		}
		let result = null;
		let currentOperator = null;

		for (let token of filteredTokens) {
			if (token === "+" || token === "-" || token === "*" || token === "/") {
				currentOperator = token;
				continue;
			}

			let value = context[token];
			value = value === undefined || value === null ? 0 : Number(value);

			if (result === null) {
				result = value;
			} else if (currentOperator) {
				switch (currentOperator) {
				case "+":
					result += value;
					break;
				case "-":
					result -= value;
					break;
				case "*":
					result *= value;
					break;
				case "/":
					if (value === 0) {
						throw new Error("Divide by 0");
					}
					result /= value;
					break;
				}
			}
		}

		return result;
	} catch (error) {
		return 0;
	}
}
